import { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { SubscriptionClient } from "../sdk";
import { ConnectionState } from "../sdk/types/transport";
import { RateConfig } from "../sdk/types/data";
import {
  MultiFeedFundingRateData,
  MultiFeedDeviationData,
  MultiFeedTWAPData,
} from "../sdk/types/api";

const RATE_SUB_CHANNELS = ["funding_rate", "deviation", "twap"] as const;

// Pure functions

/**
 * Get latest data from array or single value
 */
const getLatest = <T>(data: T | T[] | undefined): T | undefined => {
  if (!data) return undefined;
  return Array.isArray(data) ? data[0] : data;
};

/**
 * Create subscription callback key
 */
const createCallbackKey = (subChannel: string, marketId: string): string => {
  return `${subChannel}-${marketId}`;
};

/**
 * Check if subscription client is ready
 */
const isReadyToSubscribe = (client: SubscriptionClient | null): boolean => {
  return client?.isReadyForSubscriptions() ?? false;
};


/**
 * Hook for managing rate data (funding_rate, deviation, twap) with WebSocket real-time updates
 * Automatically subscribes to all rate sub-channels for a single market ID
 */
export function useRates(config: RateConfig) {
  const historyDepth = config.historyDepth ?? 10;
  const marketId = config.marketId;

  // State for each rate type (single market, so we store directly or as array for history)
  const [fundingRate, setFundingRate] = useState<
    MultiFeedFundingRateData | MultiFeedFundingRateData[] | undefined
  >(undefined);
  const [deviation, setDeviation] = useState<
    MultiFeedDeviationData | MultiFeedDeviationData[] | undefined
  >(undefined);
  const [twap, setTwap] = useState<
    MultiFeedTWAPData | MultiFeedTWAPData[] | undefined
  >(undefined);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isConnected, setIsConnected] = useState(false);

  const subscriptionClientRef = useRef<SubscriptionClient | null>(null);
  const callbacksRef = useRef<Map<string, (data: any) => void>>(new Map());
  const subscribedMarketRef = useRef<string | null>(null);
  const effectMarketIdRef = useRef<string | null>(null);

  // Create callbacks for single market (simplified - no Map needed)
  const createSingleMarketCallback = <T extends { market_id?: string | number }>(
    subChannel: "funding_rate" | "deviation" | "twap",
    historyDepth: number,
    setState: React.Dispatch<React.SetStateAction<T | T[] | undefined>>,
  ): ((data: T) => void) => {
    return (data: T) => {
      console.debug(
        `[useRates] Received ${subChannel} message for market ${marketId}:`,
        data,
      );

      setState((prev) => {
        if (historyDepth === 1) {
          return data;
        } else {
          const history = Array.isArray(prev)
            ? prev
            : prev
              ? [prev as T]
              : [];
          return [data, ...history].slice(0, historyDepth) as T | T[];
        }
      });
    };
  };

  // Subscribe to all sub-channels for the single market
  const subscribe = useCallback(() => {
    if (!marketId) {
      console.warn("[useRates] Cannot subscribe: marketId not provided");
      return;
    }

    const client = subscriptionClientRef.current;
    if (!client) {
      console.warn("[useRates] Cannot subscribe: client not available");
      return;
    }

    if (!isReadyToSubscribe(client)) {
      console.warn(
        `[useRates] Cannot subscribe to market ${marketId}: client not ready`,
      );
      return;
    }

    // Check if already subscribed to this market
    if (subscribedMarketRef.current === marketId) {
      const allSubscribed = RATE_SUB_CHANNELS.every((subChannel) => {
        const key = createCallbackKey(subChannel, marketId);
        return callbacksRef.current.has(key);
      });

      if (allSubscribed) {
        console.debug(
          `[useRates] Already subscribed to all rate channels for market ${marketId}`,
        );
        return;
      }
    }

    // Unsubscribe from previous market if different and clear state
    if (subscribedMarketRef.current && subscribedMarketRef.current !== marketId) {
      unsubscribe(true); // Clear state when switching markets
    }

    // Create all callbacks
    const callbacksToRegister: Array<{
      subChannel: typeof RATE_SUB_CHANNELS[number];
      callback: (data: any) => void;
      key: string;
    }> = [];

    for (const subChannel of RATE_SUB_CHANNELS) {
      const key = createCallbackKey(subChannel, marketId);
      if (callbacksRef.current.has(key)) {
        continue; // Already subscribed to this sub-channel
      }

      let callback: (data: any) => void;

      switch (subChannel) {
        case "funding_rate":
          callback = createSingleMarketCallback(
            subChannel,
            historyDepth,
            setFundingRate,
          );
          break;
        case "deviation":
          callback = createSingleMarketCallback(
            subChannel,
            historyDepth,
            setDeviation,
          );
          break;
        case "twap":
          callback = createSingleMarketCallback(
            subChannel,
            historyDepth,
            setTwap,
          );
          break;
      }

      callbacksToRegister.push({ subChannel, callback, key });
    }

    // Subscribe to all sub-channels together
    const subscriptionErrors: string[] = [];
    for (const { subChannel, callback, key } of callbacksToRegister) {
      try {
        callbacksRef.current.set(key, callback);
        client.subscribeMultiFeedSubChannel(subChannel, marketId, callback);
        console.debug(
          `[useRates] Subscribed to ${subChannel} for market ${marketId}`,
        );
      } catch (error) {
        console.error(
          `[useRates] Failed to subscribe to ${subChannel} for market ${marketId}:`,
          error,
        );
        // Clean up on failure
        callbacksRef.current.delete(key);
        subscriptionErrors.push(
          error instanceof Error
            ? error.message
            : `Failed to subscribe to ${subChannel}`,
        );
      }
    }

    // Set error if any subscription failed
    if (subscriptionErrors.length > 0) {
      setError(
        `Failed to subscribe to some rate channels: ${subscriptionErrors.join(", ")}`,
      );
    } else if (callbacksToRegister.length > 0) {
      // Clear error if all subscriptions succeeded
      setError(null);
      console.debug(
        `[useRates] Successfully subscribed to all rate channels (${RATE_SUB_CHANNELS.join(", ")}) for market ${marketId}`,
      );
    }

    subscribedMarketRef.current = marketId;
  }, [marketId, historyDepth]);

  // Unsubscribe from all sub-channels for the current market
  const unsubscribe = useCallback((clearState: boolean = false) => {
    const currentMarketId = subscribedMarketRef.current;
    if (!currentMarketId) return;

    const client = subscriptionClientRef.current;
    if (!client) return;

    RATE_SUB_CHANNELS.forEach((subChannel) => {
      const key = createCallbackKey(subChannel, currentMarketId);
      const callback = callbacksRef.current.get(key);
      if (callback) {
        try {
          client.unsubscribeMultiFeedSubChannel(subChannel, callback);
        } catch (err) {
          console.error(
            `[useRates] Failed to unsubscribe ${subChannel} for ${currentMarketId}:`,
            err,
          );
        }
        callbacksRef.current.delete(key);
      }
    });
    subscribedMarketRef.current = null;

    // Clear state if requested (when switching markets)
    if (clearState) {
      setFundingRate(undefined);
      setDeviation(undefined);
      setTwap(undefined);
      setError(null);
    }
  }, []);

  // Main effect: setup and subscribe
  useEffect(() => {
    const subscriptionClient = config.subscriptionClient;
    const currentMarketId = marketId;
    subscriptionClientRef.current = subscriptionClient;
    setLoading(false);
    setIsConnected(subscriptionClient.isConnected());

    // Track the marketId for this effect instance
    const previousMarketId = effectMarketIdRef.current;
    effectMarketIdRef.current = currentMarketId;

    // Clear state if marketId changed
    if (previousMarketId && previousMarketId !== currentMarketId) {
      setFundingRate(undefined);
      setDeviation(undefined);
      setTwap(undefined);
      setError(null);
    }

    const doSubscribe = () => {
      if (!isReadyToSubscribe(subscriptionClient)) {
        const authState = subscriptionClient.getAuthState();
        if (authState === "failed") {
          setError("Authentication failed - cannot subscribe to rates");
        }
        return;
      }
      subscribe();
    };

    if (isReadyToSubscribe(subscriptionClient) && currentMarketId) {
      doSubscribe();
    }

    const unsubscribeStateChange = subscriptionClient.onStateChange(
      (state: ConnectionState) => {
        setIsConnected(state === ConnectionState.Connected);
        if (state === ConnectionState.Connected && currentMarketId) {
          doSubscribe();
        }
      },
    );

    return () => {
      unsubscribeStateChange?.();
      // Always clear state when unsubscribing in cleanup (marketId or client changed)
      unsubscribe(true);
    };
  }, [config.subscriptionClient, config.baseURL, marketId, subscribe, unsubscribe]);

  // Get latest data (helper for single market)
  const latestFundingRate = useMemo(
    () => getLatest(fundingRate),
    [fundingRate],
  );

  const latestDeviation = useMemo(() => getLatest(deviation), [deviation]);

  const latestTWAP = useMemo(() => getLatest(twap), [twap]);

  return {
    // Raw data (can be single value or array for history)
    fundingRate,
    deviation,
    twap,
    // Latest data (always single value or undefined)
    latestFundingRate,
    latestDeviation,
    latestTWAP,
    // Legacy getter functions for backward compatibility (return data for current market)
    getFundingRate: useCallback(() => fundingRate, [fundingRate]),
    getLatestFundingRate: useCallback(() => latestFundingRate, [latestFundingRate]),
    getDeviation: useCallback(() => deviation, [deviation]),
    getLatestDeviation: useCallback(() => latestDeviation, [latestDeviation]),
    getTWAP: useCallback(() => twap, [twap]),
    getLatestTWAP: useCallback(() => latestTWAP, [latestTWAP]),
    loading,
    error,
    isConnected,
  };
}
